import { Injectable, inject } from '@angular/core';
import {
  AssetService,
  GridUtilsV2,
  SpinnerV2Service,
  StorageV2Service,
} from '@schenck/rotec-components';
import { combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { statusFilter } from 'src/components/status/status.component';
import {
  enhanceSortWithScheduling,
  gridSchedulingMultiselectFilter,
} from 'src/components/task-interval.pipe';
import { SearchServiceBase, SearchApi } from 'src/services/search.service';
import { MachineTaskSettingsService } from './machine-task-settings.service';
import { MachineTask } from './machine-tasks.model';
import { MachineTaskService } from 'src/services';

@Injectable()
export class MachineTaskSearchApi implements SearchApi<MachineTask> {
  private readonly taskService = inject(MachineTaskService);
  private readonly assetService = inject(AssetService);

  reload$ = this.taskService.taskChange$;

  search$(date: { from: Date; to: Date } | null) {
    return this.taskService.search$(true, date).pipe(
      switchMap((results) => {
        if (results.length === 0) {
          // necessary to handle this because combineLatest won't emit anything if it gets passed an empty array
          return of([]);
        }

        const searchResults$ = results.map((result) => {
          const machine$ = this.assetService.machine$(result.machineId);
          const plant$ = this.assetService.plantOfMachine$(machine$);

          return combineLatest([machine$, plant$]).pipe(
            map(([machine, plant]) => {
              const task: MachineTask = {
                ...result,
                plantName: (plant && plant.name) || '',
                machineSerial: (machine && machine.serial) || '',
                machineName: machine && machine.name,
              };
              return task;
            })
          );
        });

        return combineLatest(searchResults$);
      })
    );
  }
}

@Injectable()
export class MachineTaskSearchService extends SearchServiceBase<MachineTask> {
  constructor() {
    const searchApi = inject(MachineTaskSearchApi);
    const loadingService = inject(SpinnerV2Service);
    const settingsService = inject(MachineTaskSettingsService);
    const storageService = inject(StorageV2Service);

    super(searchApi, loadingService, settingsService, storageService, {
      key: 'MachineTasksSearch',
      primaryDateProp: 'dueDate',
      filter: {
        plantName: GridUtilsV2.multiselectFilter,
        machineName: GridUtilsV2.multiselectFilter,
        machineSerial: GridUtilsV2.multiselectFilter,
        schenckId: GridUtilsV2.textFilter,
        customerId: GridUtilsV2.textFilter,
        title: GridUtilsV2.textFilter,
        priority: GridUtilsV2.checkboxFilter,
        responsibleCompany: GridUtilsV2.radioFilter,
        responsible: GridUtilsV2.multiselectFilter,
        executive: GridUtilsV2.multiselectFilter,
        dueDate: GridUtilsV2.dateRangeFilter,
        status: statusFilter,
        description: GridUtilsV2.textFilter,
        component: GridUtilsV2.multiselectFilter,
        type: GridUtilsV2.multiselectFilter,
        requiredTooling: GridUtilsV2.textFilter,
        possibleSpareParts: GridUtilsV2.textFilter,
        scheduling: gridSchedulingMultiselectFilter,
      },
      searchResultComparator: enhanceSortWithScheduling(GridUtilsV2.defaultResultComparator),
    });
  }
}
